// Session against the existing Rails mobile auth (api/v3/sessions), the same
// contract the Flutter app uses (PropelRails docs/api-contract/01-authentication.md).
// The browser calls its own origin; the dev server / CloudFront proxy forwards
// /api to Rails. A cookie-based web session (api/web/v1) replaces this later.

const KEY = "pm.session.v1";
// Same-origin prefix the proxy maps to the Rails origin.
export const BACKEND = "/backend";

export interface Session {
  token: string;
  expiresAt: string | null;
  user: { id: string; email: string };
}

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    if (s.expiresAt && new Date(s.expiresAt).getTime() < Date.now()) return null;
    return s;
  } catch {
    return null;
  }
}

function saveSession(s: Session | null) {
  try {
    if (s) localStorage.setItem(KEY, JSON.stringify(s));
    else localStorage.removeItem(KEY);
  } catch {
    /* private mode: session lasts for this tab only */
  }
}

// Every request carries the headers the contract expects from a client.
export function clientHeaders(): Record<string, string> {
  return {
    Accept: "application/json",
    "X-App-Platform": "web",
    "X-App-Version": import.meta.env.VITE_APP_VERSION ?? "v2-dev",
    "X-App-Brand": "propelmapper",
    "X-Device-Id": deviceId(),
  };
}

function deviceId(): string {
  try {
    let id = localStorage.getItem("pm.device");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("pm.device", id);
    }
    return id;
  } catch {
    return "web-ephemeral";
  }
}

export class AuthError extends Error {}

export async function signIn(email: string, password: string): Promise<Session> {
  const res = await fetch(`${BACKEND}/api/v3/sessions`, {
    method: "POST",
    headers: { ...clientHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.token) throw new AuthError(body.error || "Invalid email or password");
  const s: Session = { token: body.token, expiresAt: body.expires_at ?? null, user: body.user };
  saveSession(s);
  return s;
}

export async function requestPasswordReset(email: string): Promise<void> {
  await fetch(`${BACKEND}/api/v3/sessions/forgot_password`, {
    method: "POST",
    headers: { ...clientHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
}

export async function signOut(): Promise<void> {
  const s = loadSession();
  saveSession(null);
  if (s) {
    await fetch(`${BACKEND}/api/v3/sessions`, { method: "DELETE", headers: { ...clientHeaders(), Authorization: `Bearer ${s.token}` } }).catch(() => {});
  }
}

// Authenticated JSON call through the same-origin proxy.
export async function api<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const s = loadSession();
  if (!s) throw new AuthError("Signed out");
  const res = await fetch(BACKEND + path, {
    ...init,
    headers: {
      ...clientHeaders(),
      Authorization: `Bearer ${s.token}`,
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(init.headers as Record<string, string>),
    },
  });
  if (res.status === 401) {
    saveSession(null);
    throw new AuthError("Session expired");
  }
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(body.error || body.message || `HTTP ${res.status}`), { status: res.status, body });
  return body as T;
}
